import { useEffect, useRef } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { toast } from '@hivebrite/design-system/Toast';

import { SimpleComment } from 'hb-react/frontoffice/apps/CommentsV2/components/SimpleComment';
import { Replies } from 'hb-react/frontoffice/apps/CommentsV2/components/v3/Replies';
import { type RepliesImperativeHandle } from 'hb-react/frontoffice/apps/CommentsV2/components/v3/Replies';
import { COMMENTABLE_TYPES } from 'hb-react/frontoffice/apps/CommentsV2/constants';
import { useCommentsContext } from 'hb-react/frontoffice/apps/CommentsV2/contexts/CommentsContext';
import { useRemoveFocusedCommentsSearchParams } from 'hb-react/frontoffice/apps/CommentsV2/hooks/useFocusedCommentSearchParams';
import { type Comment } from 'hb-react/frontoffice/apps/CommentsV2/types';

interface CommentWithRepliesProps {
  canDisplayEmojiPicker?: boolean;
  comment: Comment;
  currentUser: any;
  onDeleteComment: (attr: { id: number; repliesCount?: number }) => void;
  onToggleLike: (attr: { commentId: number; newLikes: unknown }) => void;
  onUpdateComment: (attr: { comment: Comment; countDelta?: number }) => void;
  onVote: (attr: { commentId: number; score: unknown; state: unknown }) => void;
}

export const CommentWithReplies = ({
  canDisplayEmojiPicker,
  comment,
  currentUser,
  onDeleteComment,
  onToggleLike,
  onUpdateComment,
  onVote,
}: CommentWithRepliesProps) => {
  const repliesRef = useRef<RepliesImperativeHandle>(null);

  const { focusedCommentId, focusedReplyId, options } = useCommentsContext();
  const removeCommentsSearchParams = useRemoveFocusedCommentsSearchParams();

  const { id: commentId } = comment;
  const hasReplies = comment.has_replies || !!comment.replies?.total;
  const isFocused =
    !focusedReplyId && String(commentId) === String(focusedCommentId);

  const handleShowReplyForm = () => {
    repliesRef.current?.focusReplyForm();
  };

  const handleCreateReply = () => {
    onUpdateComment?.({
      comment: {
        ...comment,
        has_replies: true,
      },
      countDelta: 1,
    });
  };

  const handleDeleteReply = () => {
    onUpdateComment?.({
      comment,
      countDelta: -1,
    });
  };

  const handleDeleteComment = () => {
    onDeleteComment?.({
      id: commentId,
      repliesCount: repliesRef.current?.getRepliesCount() || 0,
    });
  };

  useEffect(() => {
    if (
      String(commentId) === String(focusedCommentId) &&
      focusedReplyId &&
      !hasReplies
    ) {
      toast.error(s__('Comments|Sorry, the referred comment is not available'));
      removeCommentsSearchParams();
    }
  }, [
    commentId,
    focusedCommentId,
    focusedReplyId,
    hasReplies,
    removeCommentsSearchParams,
  ]);

  return (
    <Box flexDirection="column" data-testid="main-comment">
      <Box flexDirection="column" mb={4}>
        <SimpleComment
          canDisplayEmojiPicker={canDisplayEmojiPicker}
          comment={comment}
          currentUser={currentUser}
          isFocused={isFocused}
          onDeleteComment={handleDeleteComment}
          onReply={handleShowReplyForm}
          onToggleLike={onToggleLike}
          onUpdateComment={onUpdateComment}
          onVote={onVote}
          options={options}
        />
      </Box>

      <Replies
        commentableId={commentId}
        commentableType={COMMENTABLE_TYPES.COMMENT}
        currentUser={currentUser}
        hasReplies={hasReplies}
        onReplyCreated={handleCreateReply}
        onReplyDeleted={handleDeleteReply}
        repliesRef={repliesRef}
      />
    </Box>
  );
};
